import { useEffect, useMemo, useRef, useState } from 'react'
import type { SessionStats } from '@/types'
import { formatNumber, mlToDisplay, volumeUnit } from '@/utils/time'
import {
  AVERAGE_PERIOD_OPTIONS,
  AVERAGE_PERIOD_STORAGE_KEY,
  DEFAULT_AVERAGE_PERIOD,
  computeAverageStats,
  computePreviousAverageStats,
  computePeriodTotals,
  computePreviousPeriodTotals,
  isAveragePeriod,
  type AveragePeriod,
} from '@/utils/stats'

interface TotalStats {
  totalDrinks: number
  totalMl: number
  totalCalories: number
}

interface StatsPanelProps {
  currentSessionStats: SessionStats | null
  lastSessionStats: SessionStats | null
  totalStats: TotalStats
  totalSessions: number
  currentBAC: number
  allSessions: SessionStats[]
}

type StatsView = 'session' | 'average' | 'total'

const STATS_VIEW_STORAGE_KEY = 'ssdl:stats-view'

function loadStatsView(): StatsView {
  try {
    const stored = localStorage.getItem(STATS_VIEW_STORAGE_KEY)
    if (stored === 'session' || stored === 'average' || stored === 'total') return stored
  } catch { /* storage unavailable */ }
  return 'session'
}

function saveStatsView(view: StatsView) {
  try {
    localStorage.setItem(STATS_VIEW_STORAGE_KEY, view)
  } catch { /* storage unavailable */ }
}

function loadAveragePeriod(): AveragePeriod {
  try {
    const stored = localStorage.getItem(AVERAGE_PERIOD_STORAGE_KEY)
    if (isAveragePeriod(stored)) return stored
  } catch { /* storage unavailable */ }
  return DEFAULT_AVERAGE_PERIOD
}

function saveAveragePeriod(period: AveragePeriod) {
  try {
    localStorage.setItem(AVERAGE_PERIOD_STORAGE_KEY, period)
  } catch { /* storage unavailable */ }
}

function ComparisonIndicator({ current, previous }: { current: number; previous: number | undefined }) {
  if (previous === undefined || previous === 0) return null

  const diff = current - previous
  if (diff === 0) return null

  const isPositive = diff > 0
  const percentChange = Math.abs(Math.round((diff / previous) * 100))

  return (
    <span className={`text-xs ml-1 ${isPositive ? 'text-[var(--bac-danger)]' : 'text-[var(--bac-safe)]'}`}>
      {isPositive ? '+' : '-'}{percentChange}%
    </span>
  )
}

export default function StatsPanel({
  currentSessionStats,
  lastSessionStats,
  totalStats,
  totalSessions,
  currentBAC,
  allSessions
}: StatsPanelProps) {
  const [view, setViewState] = useState<StatsView>(loadStatsView)
  const setView = (next: StatsView) => {
    setViewState(next)
    saveStatsView(next)
  }
  const [avgPeriod, setAvgPeriod] = useState<AveragePeriod>(loadAveragePeriod)
  // Which pill's period dropdown is open (Average and Total share one period)
  const [openMenu, setOpenMenu] = useState<'average' | 'total' | null>(null)
  const menuOpen = openMenu !== null
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!menuOpen) return
    const handleMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpenMenu(null)
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenMenu(null)
    }
    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  // The in-progress session is excluded so a half-finished night doesn't drag averages down
  const sessionInProgress = currentBAC > 0
  const averageStats = useMemo(
    () => computeAverageStats(allSessions, avgPeriod, { excludeLast: sessionInProgress }),
    [allSessions, avgPeriod, sessionInProgress]
  )
  // Same-length window before the selected period (null for All Time)
  const previousAverageStats = useMemo(
    () => computePreviousAverageStats(allSessions, avgPeriod),
    [allSessions, avgPeriod]
  )

  // Totals for the selected period (in-progress session included — its drinks really happened)
  const periodTotals = useMemo(
    () => computePeriodTotals(allSessions, avgPeriod),
    [allSessions, avgPeriod]
  )
  const previousPeriodTotals = useMemo(
    () => computePreviousPeriodTotals(allSessions, avgPeriod),
    [allSessions, avgPeriod]
  )
  // All Time keeps the entry-based totals the panel has always shown
  const shownTotals = avgPeriod === 'all'
    ? {
        drinks: totalStats.totalDrinks,
        sessionCount: totalSessions,
        ml: totalStats.totalMl,
        calories: totalStats.totalCalories,
      }
    : periodTotals

  const selectPeriod = (target: 'average' | 'total', period: AveragePeriod) => {
    setAvgPeriod(period)
    saveAveragePeriod(period)
    setView(target)
    setOpenMenu(null)
  }

  const sessionDrinks = currentSessionStats?.drinkCount ?? 0
  const sessionMl = currentSessionStats?.totalMl ?? 0
  const sessionCal = currentSessionStats?.totalCalories ?? 0
  const sessionBac = currentBAC

  const pillClass = (active: boolean) =>
    `px-3 py-1 text-xs rounded-full transition-colors ${
      active
        ? 'bg-[var(--accent-teal)] text-[var(--bg-primary)] font-medium'
        : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
    }`

  const avgOption = AVERAGE_PERIOD_OPTIONS.find(o => o.id === avgPeriod) ?? AVERAGE_PERIOD_OPTIONS[0]

  const renderPeriodPill = (target: 'average' | 'total', idleLabel: string, activePrefix: string) => (
    <div className="relative">
      <button
        onClick={() => setOpenMenu(open => (open === target ? null : target))}
        className={`${pillClass(view === target)} inline-flex items-center gap-1`}
        aria-haspopup="menu"
        aria-expanded={openMenu === target}
      >
        {view === target ? `${activePrefix} · ${avgOption.short}` : idleLabel}
        <svg className="w-3 h-3" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>
      {openMenu === target && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-40 rounded-lg border border-[var(--border)] bg-[var(--bg-secondary)] shadow-lg z-20 py-1"
        >
          {AVERAGE_PERIOD_OPTIONS.map(option => {
            const selected = option.id === avgPeriod
            return (
              <button
                key={option.id}
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => selectPeriod(target, option.id)}
                className={`w-full text-left px-3 py-2 text-sm transition-colors hover:bg-[var(--bg-card)] ${
                  selected ? 'text-[var(--accent-teal)] font-medium' : 'text-[var(--text-primary)]'
                }`}
              >
                {option.label}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )

  const Toggle = (
    <div className="flex rounded-full bg-[var(--bg-secondary)] p-0.5" ref={menuRef}>
      <button onClick={() => setView('session')} className={pillClass(view === 'session')}>
        Session
      </button>
      {renderPeriodPill('average', 'Average', 'Avg')}
      {renderPeriodPill('total', 'Total', 'Total')}
    </div>
  )

  if (view === 'average') {
    return (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
            Statistics
          </h3>
          {Toggle}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-[var(--text-secondary)]">Drinks</p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              {averageStats ? formatNumber(averageStats.avgDrinks, 1) : '-'}
              {averageStats && (
                <ComparisonIndicator current={averageStats.avgDrinks} previous={previousAverageStats?.avgDrinks} />
              )}
            </p>
          </div>
          <div>
            <p className="text-sm text-[var(--text-secondary)]">Peak BAC</p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              {averageStats ? averageStats.avgPeakBac.toFixed(3) : '-'}
              {averageStats && (
                <ComparisonIndicator current={averageStats.avgPeakBac} previous={previousAverageStats?.avgPeakBac} />
              )}
            </p>
          </div>
          <div>
            <p className="text-sm text-[var(--text-secondary)]">{volumeUnit()}</p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              {averageStats ? formatNumber(mlToDisplay(averageStats.avgMl), 0) : '-'}
              {averageStats && (
                <ComparisonIndicator current={averageStats.avgMl} previous={previousAverageStats?.avgMl} />
              )}
            </p>
          </div>
          <div>
            <p className="text-sm text-[var(--text-secondary)]">Cal</p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              {averageStats ? formatNumber(averageStats.avgCalories, 0) : '-'}
              {averageStats && (
                <ComparisonIndicator current={averageStats.avgCalories} previous={previousAverageStats?.avgCalories} />
              )}
            </p>
          </div>
        </div>

        <p className="mt-3 text-xs text-[var(--text-secondary)]">
          {averageStats
            ? `Per-session average · ${avgOption.label} · ${formatNumber(averageStats.sessionCount, 0)} ${averageStats.sessionCount === 1 ? 'session' : 'sessions'}`
            : `No completed sessions · ${avgOption.label}`}
          {averageStats && previousAverageStats && avgOption.previousLabel && ` · % vs ${avgOption.previousLabel}`}
        </p>
      </div>
    )
  }

  if (view === 'total') {
    return (
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
            Statistics
          </h3>
          {Toggle}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-[var(--text-secondary)]">Drinks</p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              {formatNumber(shownTotals.drinks, 0)}
              <ComparisonIndicator current={shownTotals.drinks} previous={previousPeriodTotals?.drinks} />
            </p>
          </div>
          <div>
            <p className="text-sm text-[var(--text-secondary)]">Sessions</p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              {formatNumber(shownTotals.sessionCount, 0)}
              <ComparisonIndicator current={shownTotals.sessionCount} previous={previousPeriodTotals?.sessionCount} />
            </p>
          </div>
          <div>
            <p className="text-sm text-[var(--text-secondary)]">{volumeUnit()}</p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              {formatNumber(mlToDisplay(shownTotals.ml), 0)}
              <ComparisonIndicator current={shownTotals.ml} previous={previousPeriodTotals?.ml} />
            </p>
          </div>
          <div>
            <p className="text-sm text-[var(--text-secondary)]">Calories</p>
            <p className="text-2xl font-bold text-[var(--text-primary)]">
              {formatNumber(shownTotals.calories, 0)}
              <ComparisonIndicator current={shownTotals.calories} previous={previousPeriodTotals?.calories} />
            </p>
          </div>
        </div>

        <p className="mt-3 text-xs text-[var(--text-secondary)]">
          {avgOption.label}
          {previousPeriodTotals && avgOption.previousLabel && ` · % vs ${avgOption.previousLabel}`}
        </p>
      </div>
    )
  }

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide">
          Statistics
        </h3>
        {Toggle}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {/* Drinks */}
        <div>
          <p className="text-sm text-[var(--text-secondary)]">Drinks</p>
          <p className="text-2xl font-bold text-[var(--text-primary)]">
            {sessionDrinks}
            <ComparisonIndicator
              current={sessionDrinks}
              previous={lastSessionStats?.drinkCount}
            />
          </p>
        </div>

        {/* BAC */}
        <div>
          <p className="text-sm text-[var(--text-secondary)]">BAC</p>
          <p className="text-2xl font-bold text-[var(--text-primary)]">
            {sessionBac > 0 ? sessionBac.toFixed(3) : '-'}
            {sessionBac > 0 && lastSessionStats?.peakBac && (
              <ComparisonIndicator
                current={sessionBac}
                previous={lastSessionStats.peakBac}
              />
            )}
          </p>
        </div>

        {/* Volume */}
        <div>
          <p className="text-sm text-[var(--text-secondary)]">{volumeUnit()}</p>
          <p className="text-2xl font-bold text-[var(--text-primary)]">
            {sessionMl > 0 ? formatNumber(mlToDisplay(sessionMl), 0) : '-'}
            {sessionMl > 0 && (
              <ComparisonIndicator
                current={mlToDisplay(sessionMl)}
                previous={lastSessionStats ? mlToDisplay(lastSessionStats.totalMl) : undefined}
              />
            )}
          </p>
        </div>

        {/* Calories */}
        <div>
          <p className="text-sm text-[var(--text-secondary)]">Cal</p>
          <p className="text-2xl font-bold text-[var(--text-primary)]">
            {sessionCal > 0 ? formatNumber(sessionCal, 0) : '-'}
            {sessionCal > 0 && (
              <ComparisonIndicator
                current={sessionCal}
                previous={lastSessionStats?.totalCalories}
              />
            )}
          </p>
        </div>
      </div>
    </div>
  )
}
